/**
 * <gas-spring-finder> — the gas spring finder (docs/gas-spring-finder.md), way in "I have my old
 * spring". Markup: snippets/gas-spring-finder.liquid; result rows are clones of the <template>
 * that snippets/gas-spring-row.liquid fills.
 *
 * The spec index is its own seam (decision log §1): sections/gas-spring-index.liquid writes it,
 * the loader here reads it. It loads on the first focusin or pointerdown in the search form, or at
 * once when the URL holds a search. Every page is fetched from data-index-url, never from
 * location.search — an active facet would filter the index — with a pinned sort, and the pages are
 * checked for completeness before anything is ranked: half an index could hide the best spring.
 *
 * Ranking is spec arithmetic over that index, not search relevance, and every tolerance lives in
 * the constants below, which the tolerance line prints. Prices go through ValerydCards.money(),
 * and their ex-VAT figures through ValerydCards.net(); no other seam is crossed and no product
 * card is built here — rows are server markup filled
 * through [data-slot]. State lives in the query string: pushState on Find and Choose,
 * replaceState on a force or quantity change, restored on load and on Back.
 *
 * A spring added alone carries no properties, so it merges with the same spring added from its
 * product page. A set carries §5.1's three properties on every line. The cart is read before the
 * add, lines are told apart by what they are (variant plus properties) and never by key, and a
 * short or failed add is rolled back to the quantities read before it (§5.5).
 *
 * The end-fitting step and the picture are a second bundle, assets/gas-spring-fittings.js
 * (<gas-spring-fittings>), inserted when the index starts to load. The seam, both ways:
 * - out: kit() sets `ctx` on the step and dispatches `gs:ctx` on it whenever the chosen spring,
 *   its force or the springs per lid change. ctx holds the spring, the fittings that fit it (fits()
 *   keeps its one home here), the number search's co-hits, the springs per lid, the choice so far
 *   (`sel`), the picture to draw, and this bundle's copy, money, ex-VAT, stock and centre-to-centre
 *   helpers.
 *   It is null without a spring: the step shows itself only while it has a context;
 * - in: every choice the customer makes there comes back as a bubbling `gs:fittings` whose detail
 *   is the new `sel`: { on, same, f: [family, family], x: [fitting, fitting] } per end, a family
 *   digit or 'x' (no fitting) or '' (none yet), and the size or null. It is kept here as `fit`:
 *   the URL, the set lines and the payload are built from it.
 *
 * The length and force sliders are <gs-range> (assets/gas-spring-range.js), which keeps each in
 * step with its field; the fields stay what is read here. The one thing crossing to them is their
 * span: span() fires `gs:span` on each field, detail { min, max, step, note } from the index for
 * the chosen thread, or 0 before the index is in, which only brings the thumbs to the fields.
 *
 * Everything per element lives in mount()'s closure, so the bundle minifies: 16 KB each.
 */
(() => {
  if (customElements.get('gas-spring-finder')) return;

  // §4.10: exact within 3 mm and the same force; close within 10 mm and 10 % or 25 N of the force,
  // whichever is more.
  const EXACT_MM = 3;
  const CLOSE_MM = 10;
  const FORCE_TOL_N = 25;
  const FORCE_TOL_PCT = 10;
  const PER_LID_MAX = 6;
  // How long the fields rest before they search on their own (§2.6).
  const LIVE_MS = 400;
  // §5.2, the set's one visible cart property. Fixed and never translated: identical sets must
  // merge in every locale. sections/cart.liquid mirrors the literal; the set add sends it.
  const SET_PROPERTY = 'Gas spring set';
  // The keys that start a load even when their values fail: a link naming a spring (gs_s) runs
  // its search before this list is read.
  const SEARCH = ['gs_no', 'gs_l', 'gs_n'];
  const GS = /^gs(_|$)/;
  const LABEL = ['exact', 'close', 'nearest'];
  const BADGE = ['success', 'info', 'neutral'];

  // Printed numbers vary in case, spaces and punctuation, so both sides are compared bare.
  const abs = Math.abs;
  const normalise = (n) => String(n).toUpperCase().replace(/[^A-Z0-9]/g, '');
  const tol = (f) => Math.max(FORCE_TOL_N, (f * FORCE_TOL_PCT) / 100);
  const parse = (v, re, min, max) => {
    const m = re.exec(v.replace(/\s+/g, ''));
    const n = m ? +m[1] : NaN;
    return n >= min && n <= max ? n : NaN;
  };
  // "660 mm" and "660" are a length; "0500N", "500 N" and "500" are a force.
  const parseL = (v) => parse(v, /^(\d+)(?:mm)?$/i, 20, 3000);
  const parseN = (v) => parse(v, /^(\d+)n?$/i, 10, 10000);
  const validNo = (v) => normalise(v).length >= 3 && normalise(v).length <= 40;

  // §4.5. Same thread, and the fitting is rated for the spring's force. One exception, kept from
  // the old web shop tool (since 2023-04-05): 650 N M6 fittings on the one 700 N M6 spring,
  // 8008-0450-0700, which otherwise has no fitting at all (measured 2026-09-28: 0 without, 26
  // with). Scoped to M6: 700 N springs exist in M8 and M10 too, where no 650 N fitting does.
  const fits = (spring, fitting) =>
    fitting.thread === spring.thread &&
    (fitting.rating >= spring.F || (spring.thread === 'M6' && spring.F === 700 && fitting.rating === 650));

  // The force of a geometry nearest f; an exact tie takes the stronger.
  const snapForce = (g, f) =>
    g.springs.reduce((a, s) => {
      const d = abs(s.F - f) - abs(a.F - f);
      return d < 0 || (d === 0 && s.F > a.F) ? s : a;
    });

  // §4.8, the one JS mirror of snippets/stock-state.liquid: the quantity a set needs is only known
  // here. The index sends an untracked variant as q = 12, twice the per-lid maximum. A spring that
  // sells on at zero is made to order; any other kind is only available to order.
  const stockFor = (q, o, need, fitting) => (q >= need ? 'in' : o ? (fitting ? 'order' : 'made') : 'out');
  const state = (x, need) => stockFor(x.q, x.o, need, x.T >= 0);

  // A row graded against what was entered, where the length or the force may be missing (§2.4):
  // a missing one counts as met. Rank 0 exact, 1 close, 2 nearest; with neither, a thread alone,
  // there is nothing to grade and the rank is NaN.
  const grade = (r, L, F) => {
    r.dL = L ? r.g.L - L : 0;
    r.dF = F ? r.s.F - F : 0;
    r.ok = !F || abs(r.dF) <= tol(F);
    r.rank = L || F ? (abs(r.dL) <= EXACT_MM && !r.dF ? 0 : abs(r.dL) <= CLOSE_MM && r.ok ? 1 : 2) : NaN;
    return r;
  };

  // A row's spring, or its geometry's first while no force is chosen.
  const first = (r) => r.s || r.g.springs[0];

  // By size: label, force within tolerance, |ΔL|, shorter first, |ΔF|, length, price, SKU.
  // Millimetres and newtons are never added together. Without a force no spring is preselected:
  // the customer picks it on the row.
  const rankSprings = (geos, L, F, thread) =>
    geos
      .filter((g) => !thread || g.thread === thread)
      .map((g) => grade({ g, s: F ? snapForce(g, F) : null }, L, F))
      .sort(
        (a, b) =>
          a.rank - b.rank ||
          b.ok - a.ok ||
          abs(a.dL) - abs(b.dL) ||
          a.dL - b.dL ||
          abs(a.dF) - abs(b.dF) ||
          a.g.L - b.g.L ||
          first(a).price - first(b).price ||
          (first(a).sku < first(b).sku ? -1 : 1)
      );

  // By number: the SKU and every stored number, plus the part after a stored number's last ':'.
  // Exact after normalising, never fuzzy. An o row's SKU can be null, which must match nothing, not
  // a search for "null".
  const rankNumber = (data, no) => {
    const key = normalise(no);
    const hit = (r) =>
      [r.sku, ...r.nos].filter(Boolean).some((n) => {
        n = String(n);
        return normalise(n) === key || (n.includes(':') && normalise(n.slice(n.lastIndexOf(':') + 1)) === key);
      });
    return { s: data.springs.filter(hit), f: data.fittings.filter(hit), o: data.others.filter(hit) };
  };

  // A number's springs of one geometry against the sizes also given: every field given within the
  // close tolerances. The number still wins; a disagreement is only said (§2.6).
  const agrees = (g, springs, s) =>
    (!s.t || g.thread === s.t) && (!s.l || abs(g.L - s.l) <= CLOSE_MM) && (!s.n || springs.some((x) => abs(x.F - s.n) <= tol(s.n)));

  // §3.4, the index's row formats, field by field.
  const SPRING = 'id sku url L C F thread price q o nos'.split(' ');
  const FITTING = 'id sku url T adds rating thread price q o title img hole ball bolt nos'.split(' ');
  const OTHER = 'sku url title nos'.split(' ');
  const zip = (keys, row) => Object.fromEntries(keys.map((k, i) => [k, row[i]]));

  const slot = (el, name) => el.querySelector(`[data-slot="${name}"]`);
  const clone = (template) => template.content.firstElementChild.cloneNode(true);
  const say = (el, text) => (el.textContent = text);
  // A button's text, in its .btn__label.
  const sayLabel = (btn, text) => say(btn.firstElementChild, text);
  const make = (tag, text, href) => {
    const el = document.createElement(tag);
    if (href) el.href = href;
    say(el, text || '');
    return el;
  };
  const noop = () => {};
  // One step bundle per page, however many finders load an index.
  let kitScript;

  const mount = (root) => {
    const d = root.dataset;
    const ac = new AbortController();
    // Every [data-gs-*] hook of the shell, by name. Template contents are not walked.
    const $ = {};
    root.querySelectorAll('*').forEach((el) =>
      el.getAttributeNames().forEach((a) => a.startsWith('data-gs-') && ($[a.slice(8)] = el))
    );
    const form = $.form;
    const qty = $.qty.querySelector('input');
    const field = (name) => form.querySelector(`[name="${name}"]`);
    const thread = () => field('gs_t').value;
    const findLabel = $.submit.firstElementChild.textContent;
    const lead = +d.leadDays;
    // Thread order and diameters come from the select's options (one Liquid list, §4.4), never
    // from code.
    const threads = {};
    form.querySelectorAll('[name="gs_t"] [data-rod]').forEach((r, i) => {
      threads[r.value] = { i, rod: r.dataset.rod, tube: r.dataset.tube };
    });

    let data;
    let idx;
    let st;
    let last;
    let note = '';
    let sku = '';
    let chosen = null;
    let fit = null;
    let link = null;
    let busy = false;
    let rows = [];
    let cohits = [];

    const t = (k, vars = {}) => {
      let s = root.getAttribute('data-t-' + k) || '';
      for (const v in vars) s = s.split(`%${v}%`).join(vars[v]);
      return s;
    };
    const tn = (k, n, vars) => t(n === 1 && root.hasAttribute(`data-t-${k}-one`) ? k + '-one' : k, { COUNT: n, ...vars });
    const money = (cents) => ValerydCards.money(cents / 100, d.locale, d.currency);
    // The same amount excl. VAT under a price, as snippets/price.liquid prints it. Hidden where the
    // market's prices carry no known rate, and for no amount: net() answers null for both.
    const net = (el, cents) => {
      const n = ValerydCards.net(cents / 100);
      say(el, n === null ? '' : t('excl-vat', { PRICE: money(n * 100) }));
      el.hidden = n === null;
    };
    const nav = (push) => {
      history[push ? 'pushState' : 'replaceState'](null, '', url());
      last = location.search;
    };
    const perLid = () => Math.min(PER_LID_MAX, Math.max(1, Math.round(+qty.value) || 1));
    // §4.6, centre to centre: the spring's length plus what each end adds; a skipped end adds 0.
    const cc = (a, b, base = chosen.s.L) => Math.round((base + (a ? a.adds : 0) + (b ? b.adds : 0)) * 10) / 10;
    // The fitting at each end, or null: reused, skipped, or not chosen yet.
    const ends = () => (fit && fit.on ? [fit.x[0], fit.x[fit.same ? 0 : 1]] : [null, null]);
    // The first end the customer said yes to and has not chosen a size for, else undefined.
    const pend = () => fit && fit.on ? [0, 1].find((i) => (!i || !fit.same) && fit.f[i] !== 'x' && !fit.x[i]) : undefined;
    // §4.7, for the set's lines and the payload alike: the spring × P, then each fitted end × P, or
    // one line × 2P when both ends take the same fitting. Each part is [variant, quantity, its
    // cart part, the copy key of its end].
    const parts = () => {
      const [a, b] = ends();
      const P = perLid();
      return [[chosen.s, P, 'spring']].concat(
        a && a === b ? [[a, 2 * P, 'both_ends', 'end-both']] : [[a, P, 'tube_end', 'end-tube'], [b, P, 'rod_end', 'end-rod']].filter((p) => p[0])
      );
    };

    // ------------------------------------------------------------ fields and URL

    const err = (input, msg) => {
      const p = root.querySelector(`#${input.id}-error`);
      say(p, msg || '');
      p.hidden = !msg;
      if (msg) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
      input.closest('.field').classList.toggle('field--error', !!msg);
    };

    // The fields as a search, or null. `show` paints every error and focuses the first; `only`
    // paints one field (blur). Without either nothing is painted.
    const read = (show, only) => {
      const no = field('gs_no').value.trim();
      const l = field('gs_l').value.trim();
      const n = field('gs_n').value.trim();
      const s = { no, l: parseL(l), n: parseN(n), t: thread() };
      // Any one of number, length, force and thread is a search (§2.4); anything typed is still
      // checked. With none, the length field asks for one, and leaving another field repaints it.
      const none = !no && !l && !n && !s.t;
      const errors = [
        ['gs_no', no && !validNo(no), 'number-error'],
        ['gs_l', none || (l && !s.l), none ? 'need-one' : 'length-error'],
        ['gs_n', n && !s.n, 'force-error'],
      ];
      for (const [name, bad, key] of errors) if (show || only === field(name) || (only && !l && name == 'gs_l')) err(field(name), bad && t(key));
      const first = errors.find((e) => e[1]);
      if (!first) return s;
      if (show) field(first[0]).focus();
      return null;
    };

    // Every key starts with gs, so page, sort_by, filter.*, q and view pass through untouched.
    const url = () => {
      const u = new URLSearchParams(location.search);
      const s = st || {};
      [...u.keys()].forEach((k) => GS.test(k) && u.delete(k));
      const put = (k, v) => v && u.set(k, v);
      put('gs', 'spring');
      put('gs_no', s.no);
      put('gs_l', s.l);
      put('gs_n', s.n);
      put('gs_t', s.t);
      if (chosen) {
        put('gs_s', chosen.s.sku);
        put('gs_q', perLid());
        // §2.11: no gs_a means "reuse mine", no gs_b "the same as the tube end", x "no fitting".
        // A choice still missing a size is not written.
        if (fit && fit.on && pend() == null) {
          const k = (i) => (fit.x[i] ? fit.x[i].sku : 'x');
          put('gs_a', k(0));
          if (!fit.same) put('gs_b', k(1));
        }
      }
      return location.pathname + '?' + u;
    };

    // The URL into the fields and the view, on load and on Back. An invalid part is dropped with
    // one notice; an empty one means absent. Focus stays where it is.
    const restore = () => {
      const u = new URLSearchParams((last = location.search));
      const param = (k) => (u.get(k) || '').trim();
      // Back past the first Find: that entry holds no search, so the fields keep what was typed.
      if (![...u.keys()].some((k) => GS.test(k))) {
        note = sku = '';
        return clear();
      }
      let bad = false;
      const put = (name, ok) => {
        const v = param(name);
        if (v && !ok) bad = true;
        field(name).value = ok ? v : '';
        err(field(name), '');
      };
      put('gs_no', validNo(param('gs_no')));
      put('gs_l', parseL(param('gs_l')));
      put('gs_n', parseN(param('gs_n')));
      const th = param('gs_t');
      if (th && !threads[th]) bad = true;
      field('gs_t').value = threads[th] ? th : '';
      span();
      const q = param('gs_q');
      const okQ = Number.isInteger(+q) && +q >= 1 && +q <= PER_LID_MAX;
      if (q && !okQ) bad = true;
      // Otherwise the field's own default, rendered by the Liquid, never a second copy here.
      qty.value = okQ ? +q : qty.defaultValue;
      note = bad ? t('repaired') : '';
      sku = param('gs_s');
      // The fittings are checked against the spring once the index is in (show()).
      link = param('gs_a') && [param('gs_a'), param('gs_b')];
      // A link that names only a spring still shows it.
      st = read() || (sku && { no: '', t: '' });
      if (st) return run(false);
      clear();
      if (SEARCH.some(param)) load().catch(noop);
    };

    // ------------------------------------------------------------ the index

    // The step's bundle comes with the index: a visitor who only browses the grid loads neither,
    // and it is in by the time a spring can be chosen.
    const load = () => {
      if (!kitScript && !customElements.get('gas-spring-fittings')) {
        document.head.append((kitScript = Object.assign(document.createElement('script'), { src: d.fittingsSrc })));
      }
      return (idx ||= get().then(
        (x) => {
          data = x;
          span();
        },
        (e) => {
          idx = null;
          throw e;
        }
      ));
    };

    // One refetch with a cache-buster when a page fails or the pages do not add up, then an error.
    const get = async () => {
      for (let bust = 0; ; bust++) {
        try {
          return index(await pages(bust));
        } catch (e) {
          if (bust || ac.signal.aborted) throw e;
        }
      }
    };

    const pages = async (bust) => {
      const one = async (n) => {
        const r = await fetch(
          `${d.indexUrl}?section_id=gas-spring-index&page=${n}&sort_by=created-ascending${bust ? '&_=' + Date.now() : ''}`,
          { signal: ac.signal }
        );
        if (!r.ok) throw new Error(r.status);
        const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
        return JSON.parse(doc.querySelector('script[data-gs-index]').textContent);
      };
      const range = (from, to) => Promise.all(Array.from({ length: to - from }, (_, i) => one(from + i + 1)));
      const n = +d.indexPages || 1;
      const got = await range(0, n);
      // The first page's count is the index's own, rendered now; data-index-pages may be older (a
      // tab left open). More pages: fetch the rest once. Fewer: the extra page is past the end,
      // rendered empty, and is dropped. The pages are still checked against each other.
      const total = got[0].pages;
      return total > n ? got.concat(await range(n, total)) : got.slice(0, total);
    };

    // §3.4. A product unpublished while the pages render shifts every later one, so counting rows
    // cannot see a gap: product ids must be unique and add up to `items` on every page.
    const index = (got) => {
      const { items, pages: total } = got[0];
      const ids = new Set();
      const vids = new Set();
      let n = 0;
      let rowCount = 0;
      for (const p of got) {
        if (p.items !== items || p.pages !== total) throw new Error('pages disagree');
        n += p.ids.length;
        rowCount += p.s.length + p.f.length;
        p.ids.forEach((id) => ids.add(id));
        p.s.concat(p.f).forEach((r) => vids.add(r[0]));
      }
      if (got.length !== total || ids.size !== n || n !== items || vids.size !== rowCount) throw new Error('incomplete');

      // Rows that fail validation are dropped and counted, for the manual check on dev.
      const pos = (x) => typeof x === 'number' && x > 0 && x < Infinity;
      const cents = (x) => x === 0 || pos(x);
      const out = { springs: [], fittings: [], others: [] };
      let dropped = 0;
      const base = (r) => pos(r.id) && r.sku && r.url && threads[r.thread] && cents(r.price) && cents(r.q);
      const keep = (list, keys, rows, ok) =>
        rows.forEach((row) => {
          const r = zip(keys, row);
          r.o = !!r.o;
          r.nos ||= [];
          if (ok(r)) list.push(r);
          else dropped++;
        });
      for (const p of got) {
        keep(out.springs, SPRING, p.s, (r) => base(r) && pos(r.L) && pos(r.C) && pos(r.F) && r.C < r.L);
        keep(out.fittings, FITTING, p.f, (r) => base(r) && r.T >= 0 && r.T <= 5 && pos(r.adds) && pos(r.rating));
        // An o row's SKU is null when the product's first variant has none (the index says so).
        keep(out.others, OTHER, p.o, (r) => r.url && r.nos.length);
      }
      root.setAttribute('data-gs-dropped', dropped);
      // A geometry is a series × extended length; any force of it can be made.
      const geo = {};
      for (const s of out.springs) (geo[s.thread + '|' + s.L] ||= { thread: s.thread, L: s.L, springs: [] }).springs.push(s);
      out.geos = Object.values(geo);
      out.geos.forEach((g) => g.springs.sort((a, b) => a.F - b.F));
      return out;
    };

    // §2.4: each slider spans what the index holds for the chosen thread, every thread when "Not
    // sure"; forces step by the closest two, or 25 N when there is only one. Never a limit of its own.
    const span = () => {
      const th = thread();
      const s = data ? data.springs.filter((x) => !th || x.thread === th) : [];
      const F = [...new Set(s.map((x) => x.F))].sort((a, b) => a - b);
      const gap = Math.min(...F.slice(1).map((f, i) => f - F[i]));
      const go = (name, v, step) =>
        field(name).dispatchEvent(
          new CustomEvent('gs:span', { detail: v.length && { min: Math.min(...v), max: Math.max(...v), step, note: t(th ? 'span-thread' : 'span-all', { THREAD: th }) } })
        );
      go('gs_l', s.map((x) => x.L), 1);
      go('gs_n', F, gap < Infinity ? gap : 25);
    };

    // ------------------------------------------------------------ results

    const run = async (focus) => {
      const wait = !data;
      const busyFind = (on) => {
        sayLabel($.submit, on ? t('loading') : findLabel);
        if (on) $.submit.setAttribute('aria-busy', 'true');
        else $.submit.removeAttribute('aria-busy');
        $.results.toggleAttribute('data-loading', on);
      };
      if (wait) busyFind(true);
      let ok = true;
      try {
        await load();
      } catch (e) {
        ok = false;
      }
      if (ac.signal.aborted) return;
      if (wait) busyFind(false);
      clear();
      if (!ok) return ($.error.hidden = false);
      show();
      if (focus) $.head.focus();
    };

    const clear = () => {
      rows = [];
      chosen = fit = null;
      $.head.hidden = true;
      [$.count, $.tolerance, $.message, $.list].forEach((el) => el.replaceChildren());
      $.more.hidden = $.next.hidden = $.error.hidden = $.measure.hidden = true;
      say($.notice, note);
      set();
      kit();
    };

    // §2.6. A number takes precedence over the size fields; springs over fittings over the rest.
    const show = () => {
      const msg = [];
      const p = (text) => msg.push(make('p', text));
      let found = [];
      let count = true;
      let next = false;
      let sized = !st.no;
      cohits = [];
      if (st.no) {
        const hit = rankNumber(data, st.no);
        if (hit.s.length) {
          let agreed = false;
          for (const g of data.geos) {
            const mine = hit.s.filter((s) => g.springs.includes(s));
            if (mine.length) {
              found.push({ g, s: mine.length === 1 ? mine[0] : null, number: true, hits: mine.length });
              agreed ||= agrees(g, mine, st);
            }
          }
          if (found.length === 1 && !found[0].s) p(t('many', { NO: st.no, COUNT: found[0].hits }));
          // With no size given there is nothing to disagree with. A disagreeing row is no "size
          // that fits", so the count goes too.
          if (!(count = agreed)) p(t('mismatch', { NO: st.no }));
          // Carried to the fitting step: fittings the number also lists, of a hit spring's thread.
          cohits = hit.f.filter((f) => found.some((r) => r.g.thread === f.thread));
        } else if (hit.f.length || hit.o.length) {
          const link = (key) => (r) => {
            p('');
            msg[msg.length - 1].append(make('a', t(key, { NO: st.no, TITLE: r.title }), r.url));
          };
          hit.f.forEach(link('fitting'));
          hit.o.forEach(link('other'));
          count = false;
        } else {
          // With the sizes given they are searched below, and the message says so: a tester read
          // those rows as the number's.
          const both = st.l && st.n;
          p(both ? t('unknown-sized', { NO: st.no }) : t('unknown'));
          count = false;
          // Measure it instead, unless the sizes are given and searched below.
          $.measure.hidden = sized = both;
        }
      }
      if (sized && (st.l || st.n || st.t)) {
        const all = rankSprings(data.geos, st.l, st.n, st.t);
        // Exact and close, or every geometry of the thread when it is all that was given.
        const good = all.filter((r) => !(r.rank > 1));
        if (good.length) {
          // Thread not sure and several match: group by thread, never interleave by price.
          const spread = [...new Set(good.map((r) => r.g.thread))].sort((a, b) => threads[a].i - threads[b].i);
          if (!st.t && spread.length > 1) {
            good.sort((a, b) => threads[a.g.thread].i - threads[b.g.thread].i);
            p(t('prompt', { LIST: spread.map((th) => t('rod', { ROD: threads[th].rod, THREAD: th })).join(', ') }));
          }
          // Both sizes keep the ten best; one size or the thread alone shows all, five at a time.
          found = st.l && st.n ? good.slice(0, 10) : good;
          count = true;
        } else {
          const max = st.t && Math.max(...data.springs.filter((s) => s.thread === st.t).map((s) => s.F));
          p(max > 0 && st.n > max ? t('range', { THREAD: st.t, N: max }) : t('no-match', { MM: CLOSE_MM, PCT: FORCE_TOL_PCT, N: FORCE_TOL_N }));
          found = all.slice(0, 3);
          count = false;
          next = true;
        }
      }
      // A spring named by the link: preset its row, or say it is gone.
      let pick = null;
      if (sku) {
        const sp = data.springs.find((s) => s.sku === sku);
        if (sp && !found.length) found = [{ g: data.geos.find((g) => g.springs.includes(sp)), s: sp, number: true }];
        pick = sp && found.find((r) => r.g.springs.includes(sp));
        if (!pick) say($.notice, t(sp ? 'repaired' : 'gone'));
        else if ((pick.s = sp) && !pick.number) grade(pick, st.l, st.n);
        // The link's fittings: each must still be sold and fit this spring, or the choice goes.
        if (pick && link) {
          const one = (v) => v === 'x' || data.fittings.find((f) => f.sku === v && fits(sp, f));
          const x = [one(link[0]), one(link[1] || link[0])];
          if (x.every(Boolean)) fit = { on: true, same: !link[1], f: x.map((f) => (f.sku ? '' + f.T : 'x')), x: x.map((f) => (f.sku ? f : null)) };
          else say($.notice, t('repaired'));
        }
        sku = '';
      }
      link = null;

      rows = found;
      // For GTM (docs/analytics-tracking.md): the nearest-we-have rows are no fit, so they count 0.
      if (window.dataLayer) dataLayer.push({ event: 'gas_spring_search', search_by: st.no ? 'number' : 'size', result_count: count ? rows.length : 0 });
      $.head.hidden = false;
      say($.count, count && rows.length ? tn('count', rows.length) : '');
      // The rule is about sizes, so a thread alone prints none.
      say($.tolerance, st.no || st.l || st.n ? t('tolerance', { EXACT: EXACT_MM, CLOSE: CLOSE_MM, PCT: FORCE_TOL_PCT, N: FORCE_TOL_N }) : '');
      $.message.replaceChildren(...msg);
      $.list.replaceChildren(...rows.map((r, i) => row(r, i >= 5)));
      $.more.hidden = rows.length <= 5;
      sayLabel($.more, rows.length > 5 ? tn('more', rows.length - 5) : '');
      $.next.hidden = !next;
      if (pick) choose(pick);
    };

    const row = (r, hide) => {
      const el = clone($.rows);
      const sel = slot(el, 'force');
      const opt = (v, text) => {
        const o = make('option', text);
        o.value = v;
        sel.append(o);
      };
      if (!r.s) opt('', t('unset'));
      r.g.springs.forEach((s) => opt(s.sku, t('option', { N: s.F }) + (s.q > 0 ? '' : ' ' + t(s.o ? 'option-made' : 'option-out'))));
      sel.value = r.s ? r.s.sku : '';
      r.el = el;
      el.hidden = hide;
      fill(r);
      return el;
    };

    // Everything on a row that follows its force or the springs per lid.
    const fill = (r) => {
      const { el, g, s } = r;
      const th = threads[g.thread];
      const sp = s || g.springs[0];
      const on = chosen === r;
      const badge = slot(el, 'badge').firstElementChild;
      // Only a difference is said: a size that matches needs no line.
      const delta = (v, less, more, key) => (v ? t(v < 0 ? less : more, { [key]: Math.round(abs(v) * 10) / 10 }) : '');
      // A row of a thread alone is graded against nothing, so it carries no badge.
      slot(el, 'badge').hidden = !(r.number || r.rank >= 0);
      badge.className = 'badge badge--' + (r.number ? 'success' : BADGE[r.rank]);
      say(badge.lastElementChild, t(r.number ? 'number' : LABEL[r.rank]));
      say(slot(el, 'title'), t('title', { THREAD: g.thread, LENGTH: g.L }));
      say(slot(el, 'specs'), t('specs', { COMPRESSED: sp.C, ROD: th.rod }));
      // Shown by CSS only while the thread is "Not sure".
      say(slot(el, 'rodcheck'), t('rodcheck', { ROD: th.rod }));
      // A delta only against a size that was given.
      const dl = r.number || !st.l ? '' : delta(r.dL, 'shorter', 'longer', 'MM');
      slot(el, 'length-delta').hidden = !dl;
      say(slot(el, 'length-delta'), dl);
      say(slot(el, 'force-delta'), r.number || !s || !st.n ? '' : delta(r.dF, 'weaker', 'stronger', 'N'));
      say(slot(el, 'sku'), s ? s.sku : '');
      say(slot(el, 'price'), s ? t('per-spring', { PRICE: money(s.price) }) : '');
      net(slot(el, 'excl-vat'), s && s.price);
      stock(slot(el, 'stock'), s, perLid());
      slot(el, 'details').href = sp.url;
      say(slot(el, 'choose-label'), t(on ? 'change' : 'choose'));
      el.toggleAttribute('data-chosen', on);
    };

    // The stock text of a row, a set line or a fitting size, against the quantity the set needs.
    const label = (x, need, s = state(x, need)) =>
      s === 'in' ? t('in') : s === 'made' ? t('made') + (lead > 0 ? ' · ' + tn('lead', lead) : '') : s === 'order' ? t('order') : x.q > 0 ? tn('only', x.q) : t('out');
    const stock = (el, x, need) => {
      const s = x ? state(x, need) : '';
      el.dataset.state = s;
      slot(el, 'icon-in').hidden = s !== 'in';
      slot(el, 'icon-made').hidden = s !== 'made' && s !== 'order';
      say(slot(el, 'stock-label'), x ? label(x, need, s) : '');
      return s;
    };

    const rowOf = (el) => rows.find((r) => r.el === el.closest('[data-gs-row]'));

    // In place: price, stock, compressed length, deviation and badge. A fitting chosen for this
    // spring that is not rated for the new force leaves the set, and the customer is told (§2.6).
    const force = (r) => {
      r.s = r.g.springs.find((s) => s.sku === slot(r.el, 'force').value) || null;
      if (r.s && !r.number) grade(r, st.l, st.n);
      if (!r.s && chosen === r) chosen = fit = null;
      if (chosen === r && fit) {
        fit.x = fit.x.map((x, i) => {
          if (!x || fits(r.s, x)) return x;
          // Only a fitting in the set is announced: not one set aside with "reuse mine", nor the
          // rod end's last choice while both ends are the same.
          if (fit.on && (!i || !fit.same)) say($.notice, t('refit', { SKU: x.sku, N: r.s.F }));
          return null;
        });
      }
      fill(r);
      set();
      kit();
      nav();
    };

    // Choose, or Change on the chosen row. `push` is a customer's click, which starts the fitting
    // step afresh; without it the choice comes from the URL, and neither history nor focus moves.
    const choose = (r, push) => {
      if (!r.s) return slot(r.el, 'force').focus();
      const prev = chosen;
      chosen = push && prev === r ? null : r;
      if (push) fit = null;
      if (prev) fill(prev);
      fill(r);
      set();
      // A customer's choice opens the fitting step (the step bundle reads `fresh`).
      kit(push);
      if (!push) return;
      nav(chosen);
      (chosen ? ($.fittings.hidden ? $.sethead : $.fithead) : $.head).focus();
    };

    // The fitting step's context (the seam in the header), null without a spring; the step shows
    // itself only while it has one, so a step whose bundle never ran asks nothing. A step not yet
    // upgraded reads it on upgrade. The co-hits are those of the spring's thread, whatever their
    // rating: §2.6 counts them to decide whether one end can be guessed, and a co-hit the force
    // rules out still means the old spring did not carry one fitting at both ends.
    const kit = (fresh) => {
      const s = chosen && chosen.s;
      $.fittings.ctx = s && { fresh, s, list: data.fittings.filter((f) => fits(s, f)), cohits: cohits.filter((f) => f.thread === s.thread), no: st.no, need: perLid(), sel: fit, locale: d.locale, diagram: $.diagram, t, money, net, state, label, stock, cc };
      $.fittings.dispatchEvent(new Event('gs:ctx'));
    };

    // ------------------------------------------------------------ the set

    const set = () => {
      $.set.toggleAttribute('data-empty', !chosen);
      $.empty.hidden = !!chosen;
      $.body.hidden = !chosen;
      say($.outcome, '');
      $.cart.hidden = true;
      if (!chosen) return;
      const [a, b] = ends();
      const i = pend();
      let reason = i == null ? '' : fit.same ? t('need-both') : t('need-fitting', { END: t(i ? 'end-rod' : 'end-tube') });
      let total = 0;
      let made = false;
      let order = false;
      // §2.9: the spring, then a line per fitted end; the same fitting at both ends is one line.
      $.lines.replaceChildren(
        ...parts().map(([x, n, , end]) => {
          const li = clone($.lineitem);
          const v = stock(slot(li, 'stock'), x, n);
          say(slot(li, 'label'), end ? t('line-fit', { COUNT: n, FAMILY: t('fam-' + x.T), SKU: x.sku, END: t(end) }) : tn('line', n, { SKU: x.sku }));
          say(slot(li, 'price'), money(x.price * n));
          net(slot(li, 'excl-vat'), x.price * n);
          total += x.price * n;
          made ||= v === 'made';
          order ||= v === 'order';
          if (v === 'out' && !reason) reason = x.q > 0 ? tn('short', x.q, { SKU: x.sku }) : t('out');
          return li;
        })
      );
      say($.total, money(total));
      net($.net, total);
      say($.summary, made ? (lead > 0 ? tn('has-lead', lead) : t('has-made')) : reason || order ? '' : t('all-in'));
      say($.reason, reason);
      $.reason.hidden = !reason;
      $.fitted.hidden = !(a || b);
      $.add.disabled = !!reason || busy;
      if (!busy) sayLabel($.add, tn(a || b ? 'add-set' : 'add-spring', perLid()));
    };

    // §5.5. A line is what it is — variant id plus every property, keys sorted — because Shopify
    // changes a line's key whenever the line changes.
    const add = async () => {
      if (busy || !chosen) return;
      const s = chosen.s;
      const [a, b] = ends();
      const both = a && a === b;
      const pieces = parts();
      // §5.4, before any request: a size at every end said yes to, every fitting rated for the
      // spring, and every part buyable in its §4.7 quantity. Whether a part is still in the index
      // is not asked: the index is loaded once and never replaced, and every part comes from it.
      if (pend() != null || pieces.some(([x, n], i) => (i && !fits(s, x)) || state(x, n) === 'out')) return set();
      // §5.1–§5.2: the key names the end fittings, `-` for a skipped end; the visible value is
      // built from fixed English tokens, never the locale, so identical sets merge everywhere.
      const key = [s.sku, a ? a.sku : '-', b ? b.sku : '-'].join('|');
      const value = [s.sku]
        .concat(both ? 'both ends ' + a.sku : [['tube end', a], ['rod end', b]].map(([e, x]) => e + ' ' + (x ? x.sku : 'none')))
        .concat(cc(a, b) + ' mm centre to centre', 'fittings fitted')
        .join(' | ');
      const items = pieces.map(([x, n, part]) => (a || b ? { id: x.id, quantity: n, properties: { _gs_set: key, _gs_part: part, [SET_PROPERTY]: value } } : { id: x.id, quantity: n }));
      const who = (id, props) => id + JSON.stringify(Object.keys(props || {}).sort().map((k) => [k, props[k]]));
      const want = items.map((i) => who(i.id, i.properties));
      const held = (lines, k) => lines.filter((l) => who(l.variant_id, l.properties) === k);
      const sum = (lines, k) => held(lines, k).reduce((a, l) => a + l.quantity, 0);
      // Every identity holds what it held before plus what was asked for.
      const full = (lines) => want.every((k, i) => sum(lines, k) >= prior[i] + items[i].quantity);
      // Only the snapshot, read before anything is written, is aborted with the finder. The add
      // and what follows it are not, on purpose: an aborted POST can still land, and a cart left
      // unreconciled could hold half a set.
      const cart = async (signal) => {
        const r = await fetch(d.cartUrl + '.js', { signal });
        if (!r.ok) throw new Error(r.status);
        return (await r.json()).items || [];
      };
      const post = (path, body) =>
        fetch(path + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      busy = true;
      $.add.disabled = true;
      $.add.setAttribute('aria-busy', 'true');
      sayLabel($.add, t('adding'));

      let prior;
      try {
        const before = await cart(ac.signal);
        prior = want.map((k) => sum(before, k));
      } catch (e) {
        return done('unreachable');
      }
      let status = 0;
      try {
        const r = await post(d.cartAddUrl, { items });
        status = r.status;
        if (r.ok && full((await r.json()).items || [])) return (location.href = d.cartUrl);
      } catch (e) {
        // Anything short of a full add is reconciled against the cart as it now stands.
      }
      let now;
      try {
        now = await cart();
      } catch (e) {
        return done('unreachable');
      }
      // The answer can be lost after the cart took everything: that is a full add all the same.
      if (full(now)) return (location.href = d.cartUrl);
      // A line split in two (a discount can do it) cannot be rolled back safely.
      if (want.some((k) => held(now, k).length > 1)) return done('partial');
      // The part to name: the first whose quantity fell short.
      const short = want.findIndex((k, i) => sum(now, k) - prior[i] < items[i].quantity);
      const updates = {};
      want.forEach((k, i) => {
        const [line] = held(now, k);
        if (line && line.quantity !== prior[i]) updates[line.key] = prior[i];
      });
      const moved = Object.keys(updates).length;
      if (moved) {
        try {
          if (!(await post(d.cartUpdateUrl, { updates })).ok) throw new Error('update');
        } catch (e) {
          return done('partial');
        }
      }
      // A part is named only when the platform refused a quantity (422) or one came up short beside
      // parts that went in. Nothing moved otherwise means the request never landed, which says
      // nothing about stock.
      done(moved || status === 422 ? 'failed' : 'lost', pieces[short][0].sku);
    };

    const done = (kind, part) => {
      busy = false;
      $.add.removeAttribute('aria-busy');
      set();
      say($.outcome, t(kind, { PART: part }));
      // Nothing was added, so there is nothing to check in the cart.
      $.cart.hidden = kind === 'failed' || kind === 'lost';
      $.outcome.focus();
    };

    // ------------------------------------------------------------ events

    // §2.6: a length, force or thread changed searches again on its own, once the customer pauses:
    // quietly, replacing the history entry, and without moving focus.
    let wait;
    const live = () => {
      clearTimeout(wait);
      wait = setTimeout(() => {
        const s = read();
        if (!s) return;
        st = s;
        note = sku = '';
        nav();
        run(false);
      }, LIVE_MS);
    };

    const on = {
      submit(e) {
        // Only the search form: the calculator inside has forms of its own (its close is one).
        if (e.target !== form) return;
        e.preventDefault();
        const s = read(true);
        if (!s) return;
        // A search from the number's dialog shows its results under it.
        $.number.close();
        st = s;
        note = '';
        chosen = null;
        nav(true);
        run(true);
      },
      click(e) {
        const b = e.target.closest('button');
        if (!b) return;
        if (b.matches('[data-slot="choose"]')) choose(rowOf(b), true);
        else if (b === $.more) {
          rows.forEach((r) => (r.el.hidden = false));
          // Before the button goes: focus left on a hidden button falls to the page.
          slot(rows[5].el, 'title').focus();
          b.hidden = true;
        } else if (b === $.retry) run(true);
        else if (b === $.measure) field('gs_l').focus();
        else if (b === $.add) add();
      },
      change(e) {
        if (e.target === qty) {
          qty.value = perLid();
          rows.forEach(fill);
          set();
          kit();
          nav();
        } else if (e.target.matches('[data-slot="force"]')) force(rowOf(e.target));
        else if (e.target.name === 'gs_t') span(live());
        // A field changed from outside it, by its slider or the calculator, is checked as if left.
        else on.focusout(e);
      },
      input: (e) => /^gs_[ln]$/.test(e.target.name) && live(),
      // The customer's choice in the fitting step (the seam in the header).
      'gs:fittings'(e) {
        fit = e.detail;
        set();
        nav();
      },
      // Only the search form: the help, the way down to the grid and the set are browsing.
      focusin: (e) => $.form.contains(e.target) && load().catch(noop),
      pointerdown: (e) => on.focusin(e),
      // On blur, never while typing — and only once the field holds something or shows an error,
      // so tabbing through an empty form does not paint it red.
      focusout(e) {
        const i = e.target;
        if (/^gs_(no|l|n)$/.test(i.name) && (i.value || i.hasAttribute('aria-invalid'))) read(false, i);
      },
    };
    const handle = (e) => on[e.type](e);
    // A fragment link (Browse all … below) fires popstate too, with the query unchanged.
    const pop = () => location.search !== last && restore();
    for (const type in on) root.addEventListener(type, handle);
    window.addEventListener('popstate', pop);
    restore();

    return () => {
      clearTimeout(wait);
      ac.abort();
      for (const type in on) root.removeEventListener(type, handle);
      window.removeEventListener('popstate', pop);
    };
  };

  class GasSpringFinder extends HTMLElement {
    connectedCallback() {
      this.setAttribute('data-js', '');
      this.unmount = mount(this);
    }

    disconnectedCallback() {
      this.unmount();
    }
  }
  customElements.define('gas-spring-finder', GasSpringFinder);
})();
